import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MailIcon from '@mui/icons-material/MailOutline';
import { Avatar, Badge, Box, Button, Chip, Container, Dialog, IconButton, InputBase, Link, Paper, Popover, Stack, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import moment from "moment";
import { decode } from 'quoted-printable';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { DashboardsContext } from "../../contexts/DashboardsContext";
import { FileStructureContext } from "../../contexts/FileStructureContext";
import { InboxContext } from "../../contexts/InboxContext";
import { scrollbarStyle } from "../../shared/dashboard";
import AuditDocumentsSection from "../molecules/audit/AuditDocumentsSection";
import EmailDocumentsSection from "../molecules/audit/EmailDocumentsSection";
import { Attachment, CompanyFile, Dashboard, EmailBody } from "../../types/files";
import CloseIcon from "@mui/icons-material/Close";
import { FileViewerOrganism } from "../modals/tables/dashboards/rows/DocumentsModal";
import { addDashboardQueryFunc, updateDashboardFunc } from "../../lib/helper";
import { DashboardQuery, DashboardQuerySource } from "../../types/files";
import { AnswerQuestionResponseType } from "../../types/search";
import { GroupSettingsContext } from "../../contexts/GroupSettingsContext";
import MultiSelector from "../atoms/MultiSelector";
import { produce } from "immer";
import ObjectUtils from "../../utils/ObjectUtils";
import ArrayUtils from "../../utils/ArrayUtils";

// ─── Types ────────────────────────────────────────────────────────────────────

/** One card in the left panel = one email with N extracted companies */
type AuditEmail = {
    emailBody: EmailBody;
    dashboards: Dashboard[];
};

// ─── Constants ────────────────────────────────────────────────────────────────

const EMAIL_TYPE_COLORS: Record<string, string> = {
    "new-deals": "#3B82F6",
    "new-deal": "#3B82F6",
    "company-updates": "#F59E0B",
    "company-update": "#F59E0B",
    "shared-pipelines": "#8B5CF6",
    "shared-pipeline": "#8B5CF6",
    "new-fund-opportunities": "#10B981",
    "new-fund-opportunity": "#10B981",
};

const EMAIL_TYPE_LABELS: Record<string, string> = {
    "new-deals": "New deal",
    "new-deal": "New deal",
    "company-updates": "Company update",
    "company-update": "Company update",
    "new-fund-opportunities": "New fund opportunity",
    "new-fund-opportunity": "New fund opportunity",
    "shared-pipelines": "Shared pipeline",
    "shared-pipeline": "Shared pipeline",
};

const FILTER_TABS = ["All", "New deals", "Company updates", "New fund opportunities", "Shared pipelines"] as const;
type FilterTab = typeof FILTER_TABS[number];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toTypeKey = (s: string) => s.toLowerCase().replace(/[\s_]+/g, "-");

const normalizeEmailType = (raw: string | null | undefined, classification?: string | null): string => {
    const src = classification || raw;
    if (!src) return "";
    return EMAIL_TYPE_LABELS[toTypeKey(src)] || "";
};

const getEmailTypeColor = (raw: string | null | undefined, classification?: string | null): string => {
    const src = classification || raw;
    if (!src) return "#6B7280";
    return EMAIL_TYPE_COLORS[toTypeKey(src)] || "#6B7280";
};

const normalizeAttachmentToken = (value: string): string => value.trim().toLowerCase();

const isExternalUrl = (value: string): boolean => {
    try {
        const protocol = new URL(value).protocol;
        return protocol === "http:" || protocol === "https:" || protocol === "ftp:" || protocol === "ftps:";
    } catch {
        return false;
    }
};

const getAttachmentNameSet = (emailBody?: EmailBody | null): Set<string> =>
    new Set(
        (emailBody?.attachments ?? [])
            .filter((attachment): attachment is string => typeof attachment === "string")
            .map(normalizeAttachmentToken)
            .filter(Boolean),
    );

const getAttachedLinks = (emailBody?: EmailBody | null): string[] => {
    const linksFromExternalLinks = (emailBody?.externalLinks ?? [])
        .filter((link): link is string => typeof link === "string")
        .map(link => link.trim())
        .filter(link => !!link && isExternalUrl(link));

    const linksFromAttachments = (emailBody?.attachments ?? [])
        .filter((attachment): attachment is string => typeof attachment === "string")
        .map(link => link.trim())
        .filter(link => !!link && isExternalUrl(link));

    return Array.from(new Set([...linksFromExternalLinks, ...linksFromAttachments]));
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
    root: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F9FAFB",
        maxWidth: "unset !important",
        padding: "unset",
    },

    // Header
    header: {
        background: "#fff",
        borderBottom: "1px solid #E5E7EB",
        padding: "14px 24px",
        flexShrink: 0,
    },
    headerTitle: {
        fontSize: "1.5rem",
        fontWeight: 700,
        color: "#111827",
        fontFamily: "Inter, sans-serif",
    },
    headerSubtitle: {
        fontSize: "0.95rem",
        color: "#6B7280",
        fontFamily: "Inter, sans-serif",
        marginTop: 2,
    },
    headerStats: {
        fontSize: "1rem",
        color: "#6B7280",
        fontFamily: "Inter, sans-serif",
        whiteSpace: "nowrap",
    },
    searchBox: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#F9FAFB",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: "5px 12px",
        height: 38,
        width: 300,
    },
    searchInput: {
        fontSize: "1rem",
        color: "#374151",
        flex: 1,
    },

    // Filter tabs
    filterTabs: {
        background: "linear-gradient(to bottom right, #f8fafc, #f1f5f9)",
        borderBottom: "1px solid #E5E7EB",
        padding: "0 24px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
    },
    filterTabsIsland: {
        background: "#fff",
        padding: 8,
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        margin: "10px 0",
        width: "100%",
    },
    filterTabsRoot: {
        minHeight: "40px",
        "& .MuiTabs-indicator": {
            display: "none",
        },
    },
    filterTab: {
        textTransform: "none" as const,
        minHeight: "40px",
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
        fontWeight: 500,
        color: "#7c818d",
        padding: "4px 16px",
        marginRight: "8px",
        borderRadius: "8px",
        transition: "all 0.2s",
        "&:hover": {
            background: "#f1f5f9",
            color: "#334155",
        },
        "&.Mui-selected": {
            background: "linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)",
            color: "#fff",
            "&:hover": {
                background: "linear-gradient(90deg, #17b7a5 0%, #0f766e 100%)",
                color: "#fff",
            },
        },
    },
    filterTabBadge: {
        "& .MuiBadge-badge": {
            background: "#e2e8f0",
            color: "#64748b",
            fontSize: "13px",
            fontWeight: 600,
            height: "16px",
            minWidth: "22px",
            padding: "0 6px",
            borderRadius: "6px",
            marginLeft: "8px",
            position: "relative",
            transform: "none",
        },
    },
    filterTabBadgeSelected: {
        "& .MuiBadge-badge": {
            background: "rgba(255, 255, 255, 0.25)",
            color: "#fff",
            fontSize: "13px",
            fontWeight: 600,
            height: "16px",
            minWidth: "22px",
            padding: "0 6px",
            borderRadius: "6px",
            marginLeft: "8px",
            position: "relative",
            transform: "none",
        },
    },

    // Body panels
    body: {
        flex: 1,
        display: "flex",
        overflow: "hidden",
    },

    // Panel 1 – Left index (timeline style, matches EmailsModal)
    leftPanel: {
        width: 400,
        borderRight: "1px solid #e5e7eb",
        background: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        overflowY: "auto",
        ...scrollbarStyle,
    },
    leftPanelHeader: {
        padding: "12px 16px",
        borderBottom: "1px solid #e5e7eb",
        background: "#fff",
        flexShrink: 0,
        position: "sticky",
        top: 0,
        zIndex: 2,
        maxHeight: 62,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
    },
    leftPanelLabel: {
        fontSize: "11px",
        fontWeight: 700,
        color: "#9ca3af",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        fontFamily: "Inter, sans-serif",
        marginBottom: 4,
    },
    leftPanelCount: {
        fontSize: "20px",
        color: "#111827",
        fontWeight: 700,
        fontFamily: "Inter, sans-serif",
    },
    leftPanelList: {
        padding: "16px 12px 16px 16px",
    },

    // Timeline date group
    dateGroup: {
        marginBottom: 24,
        position: "relative",
        "&::before": {
            content: '""',
            position: "absolute",
            left: 5,
            top: 24,
            bottom: 0,
            width: 2,
            backgroundColor: "#d1d5db",
        },
    },
    dateHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 0 4px 20px",
        marginBottom: 8,
        position: "relative",
        "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: "#14b8a6",
            boxShadow: "0 0 0 3px rgba(20, 184, 166, 0.1)",
        },
    },
    dateText: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#6b7280",
        textTransform: "uppercase",
        fontFamily: "Inter, sans-serif",
    },
    dateRelative: {
        fontSize: "12px",
        color: "#9ca3af",
        fontWeight: 400,
        marginLeft: 8,
        fontFamily: "Inter, sans-serif",
    },
    dateBadge: {
        backgroundColor: "#14b8a6",
        color: "white",
        padding: "4px 12px",
        borderRadius: 6,
        fontSize: "12px",
        fontWeight: 600,
        fontFamily: "Inter, sans-serif",
    },
    timelineItem: {
        display: "flex",
        padding: 0,
        marginLeft: 20,
        marginBottom: 8,
        cursor: "pointer",
        transition: "all 0.2s",
    },
    timelineContent: {
        flex: 1,
        minWidth: 0,
        backgroundColor: "white",
        borderRadius: 8,
        padding: "12px 16px",
        border: "2px solid transparent",
        transition: "all 0.2s",
        "&:hover": {
            backgroundColor: "#f3f4f6",
        },
    },
    timelineContentSelected: {
        borderColor: "#14b8a6",
        backgroundColor: "#f0fdfa",
    },
    timelineEmailSubject: {
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        marginBottom: 6,
    },
    timelineEmailSubjectText: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#1a1a1a",
        lineHeight: 1.4,
        wordBreak: "break-word",
        fontFamily: "Inter, sans-serif",
    },
    timelineEmailFrom: {
        fontSize: "14px",
        color: "#6b7280",
        marginBottom: 4,
        fontFamily: "Inter, sans-serif",
    },
    timelineEmailPreview: {
        fontSize: "14px",
        color: "#9ca3af",
        lineHeight: 1.4,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        fontFamily: "Inter, sans-serif",
    },

    // Company chip in left panel
    companyChip: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        border: "1.5px solid #E5E7EB",
        borderRadius: 7,
        padding: "6px 10px",
        cursor: "pointer",
        background: "transparent",
        transition: "all 0.1s",
    },
    companyChipActive: {
        background: "#DCFCE7",
        borderColor: "#34D399",
    },

    // Panel 2 – Email body (matches EmailsModal EmailViewerOrganism)
    centerPanel: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        background: "#f9fafb",
        overflow: "hidden",
    },
    centerPanelHeader: {
        padding: "12px 16px",
        background: "white",
        borderBottom: "1px solid #e5e7eb",
        flexShrink: 0,
        minHeight: 62,
        boxSizing: "border-box",
    },
    centerPanelHeaderTitle: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#1a1a1a",
        fontFamily: "Inter, sans-serif",
    },
    centerPanelContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
    },
    centerPanelScrollArea: {
        flex: 1,
        overflowY: "auto",
        padding: 24,
        ...scrollbarStyle,
    },
    centerPanelFrame: {
        width: "100%",
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        padding: 32,
        maxWidth: 800,
        margin: "0 auto",
        position: "relative",
    },
    centerPanelFrameFade: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 120,
        background: "linear-gradient(to bottom, rgba(249,250,251,0) 0%, rgba(249,250,251,1) 100%)",
        pointerEvents: "none",
        zIndex: 1,
    },
    centerPanelFrameFadeTop: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 80,
        background: "linear-gradient(to top, rgba(249,250,251,0) 0%, rgba(249,250,251,1) 100%)",
        pointerEvents: "none",
        zIndex: 1,
    },
    centerPanelCompaniesFooter: {
        flexShrink: 0,
        background: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    centerPanelCompaniesFooterInner: {
        overflowY: "auto",
        padding: "8px 24px 20px",
        flex: 1,
        ...scrollbarStyle,
    },
    resizeHandle: {
        height: 6,
        flexShrink: 0,
        background: "#e5e7eb",
        cursor: "ns-resize",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        userSelect: "none",
        transition: "background 0.15s",
        "&:hover": {
            background: "#d1d5db",
        },
        "&:active": {
            background: "#9ca3af",
        },
    },
    resizeHandleDots: {
        width: 32,
        height: 3,
        borderRadius: 2,
        background: "#9ca3af",
        pointerEvents: "none",
    },
    centerPanelEmpty: {
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 10,
        color: "#9ca3af",
        background: "#f9fafb",
        textAlign: "center",
        padding: "60px 20px",
    },
    // Email header inside the frame
    emailHeaderInfo: {
        paddingBottom: 20,
        marginBottom: 0,
    },
    emailSubject: {
        fontSize: "20px",
        fontWeight: 600,
        color: "#1a1a1a",
        marginBottom: 16,
        fontFamily: "Inter, sans-serif",
    },
    emailMeta: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
    },
    emailMetaRow: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: "14px",
    },
    emailMetaLabel: {
        fontWeight: 600,
        color: "#6b7280",
        minWidth: 60,
        fontSize: "14px",
        fontFamily: "Inter, sans-serif",
    },
    emailMetaValue: {
        color: "#1a1a1a",
        fontSize: "14px",
        fontFamily: "Inter, sans-serif",
    },
    emailBodyText: {
        fontSize: "14px",
        lineHeight: 1.6,
        color: "#1a1a1a",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontFamily: "Inter, sans-serif",
    },

    // Extracted companies section
    extractedLabel: {
        fontSize: "0.75rem",
        fontWeight: 700,
        color: "#9CA3AF",
        letterSpacing: "0.09em",
        textTransform: "uppercase",
        fontFamily: "Inter, sans-serif",
        marginBottom: 10,
    },
    extractedCard: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        padding: "12px 16px",
        cursor: "pointer",
        transition: "all 0.12s",
        marginBottom: 8,
    },
    extractedCardActive: {
        background: "#F0FDFA",
        border: "1.5px solid #0D9488",
    },

    // Panel 3 – Company detail
    rightPanel: {
        width: 400,
        borderLeft: "1px solid #E5E7EB",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        overflow: "hidden",
    },
    rightPanelEmpty: {
        width: 400,
        borderLeft: "1px solid #E5E7EB",
        background: "#F9FAFB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 8,
        flexShrink: 0,
    },
    rightPanelHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderBottom: "1px solid #F3F4F6",
        flexShrink: 0,
        minHeight: 62,
        boxSizing: "border-box",
    },
    rightPanelBadges: {
        padding: "8px 16px",
        borderBottom: "1px solid #F3F4F6",
        background: "#FAFAFA",
        flexShrink: 0,
    },
    rightPanelContent: {
        flex: 1,
        overflowY: "auto",
        ...scrollbarStyle,
    },
    rightPanelSection: {
        padding: "12px 16px",
        borderBottom: "1px solid #F3F4F6",
    },
    rightPanelSectionLabel: {
        fontSize: "0.75rem",
        fontWeight: 700,
        color: "#9CA3AF",
        letterSpacing: "0.09em",
        textTransform: "uppercase",
        fontFamily: "Inter, sans-serif",
        marginBottom: 8,
    },
    companyDetailsTitle: {
        fontSize: "1.05rem",
        fontWeight: 700,
        color: "#111827",
        fontFamily: "Inter, sans-serif",
        marginBottom: 8,
    },
    companyDetailsText: {
        fontSize: "0.9rem",
        lineHeight: 1.55,
        color: "#1f2937",
        fontFamily: "Inter, sans-serif",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    rightPanelCta: {
        padding: "12px 16px",
        borderTop: "1px solid #F3F4F6",
        flexShrink: 0,
    },
    dataRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "5px 0",
    },
    dataRowLabel: {
        fontSize: "0.875rem",
        color: "#9CA3AF",
        fontWeight: 500,
        fontFamily: "Inter, sans-serif",
    },
    dataRowValue: {
        fontSize: "0.875rem",
        color: "#111827",
        fontWeight: 500,
        fontFamily: "Inter, sans-serif",
        textAlign: "right",
        maxWidth: 168,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    founderCard: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#F9FAFB",
        border: "1px solid #F3F4F6",
        borderRadius: 8,
        padding: "8px 10px",
        marginBottom: 6,
    },
    tagChip: {
        fontSize: "0.875rem",
        color: "#6B7280",
        background: "#F9FAFB",
        border: "1px solid #E5E7EB",
        borderRadius: 4,
        padding: "3px 8px",
        fontFamily: "Inter, sans-serif",
    },

    // Shared atoms
    dot: {
        width: 7,
        height: 7,
        borderRadius: "50%",
        display: "inline-block",
        flexShrink: 0,
    },
    avatarCircle: {
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        color: "#fff",
        flexShrink: 0,
        letterSpacing: "-0.5px",
    },
    statusBadge: {
        fontSize: "0.875rem",
        color: "#374151",
        background: "#F3F4F6",
        border: "1px solid #E5E7EB",
        borderRadius: 5,
        padding: "3px 10px",
        fontWeight: 500,
        fontFamily: "Inter, sans-serif",
    },
    openRecordBtn: {
        width: "100%",
        background: "#0D9488",
        color: "#fff",
        border: "none",
        borderRadius: 8,
        padding: "9px 0",
        fontSize: "0.8rem",
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "Inter, sans-serif",
        transition: "background 0.15s",
        "&:hover": {
            background: "#0f766e",
        },
    },
    emptyText: {
        fontSize: "0.95rem",
        fontWeight: 500,
        color: "#9CA3AF",
        fontFamily: "Inter, sans-serif",
    },
    metaDivider: {
        color: "#D1D5DB",
        fontSize: "0.9rem",
    },
    metaText: {
        fontSize: "0.9rem",
        color: "#6B7280",
        fontFamily: "Inter, sans-serif",
    },
}));

// ─── Atom: DealSourceRow ─────────────────────────────────────────────────────

const DealSourceRow: React.FC<{ name: string; email?: string }> = ({ name, email }) => {
    const initials = name ? name.split(' ').slice(0, 2).map((w: string) => w.charAt(0).toUpperCase()).join('') : '';
    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{
                width: 48, height: 48,
                background: 'linear-gradient(90deg, #60a5fa 0%, #2563eb 100%)',
                fontWeight: 600, fontSize: 16,
            }}>
                {initials}
            </Avatar>
            <Box flex={1}>
                <Typography fontSize={15} fontWeight={600} color="#2c3e50">{name}</Typography>
                {!!email && (
                    <Link href={`mailto:${email}`} fontSize={13} sx={{ mt: 0.25, display: 'block' }}>{email}</Link>
                )}
            </Box>
        </Stack>
    );
};

// ─── Atom: StatusBadge ───────────────────────────────────────────────────────

const StatusBadgeAtom: React.FC<{ label: string }> = ({ label }) => {
    const classes = useStyles();
    const display = useMemo(
        () => (label ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase() : label),
        [label],
    );
    return <Box component="span" className={classes.statusBadge}>{display}</Box>;
};

// ─── Atom: StageBadge ────────────────────────────────────────────────────────

const STAGE_COLOR_MAP: Record<string, string> = {
    seed: "#10B981",
    "series a": "#3B82F6",
    "series b": "#6366F1",
    "pre-seed": "#F59E0B",
};

const StageBadgeAtom: React.FC<{ stage: string }> = ({ stage }) => {
    const c = STAGE_COLOR_MAP[stage.toLowerCase()] ?? "#6B7280";
    return (
        <Box component="span" sx={{
            fontSize: "0.875rem", color: c, background: `${c}20`,
            border: `1px solid ${c}40`, borderRadius: 1.5, px: "10px", py: "3px",
            fontWeight: 500, fontFamily: "Inter, sans-serif",
        }}>
            {stage}
        </Box>
    );
};

// ─── Atom: ConfidencePill ────────────────────────────────────────────────────

// ─── Atom: TagChip ───────────────────────────────────────────────────────────

const TagChipAtom: React.FC<{ label: string }> = ({ label }) => {
    const classes = useStyles();
    return <Box component="span" className={classes.tagChip}>{label}</Box>;
};

const CustomFieldRow: React.FC<{ field: { label: string; value: string }; isLast: boolean }> = ({ field, isLast }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [isClamped, setIsClamped] = React.useState(false);
    const textRef = React.useRef<HTMLSpanElement>(null);
    const isNoData = field.value === "No data";

    React.useLayoutEffect(() => {
        const el = textRef.current;
        if (el) setIsClamped(el.scrollHeight > el.clientHeight);
    }, [field.value, expanded]);

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1px 1fr',
            borderBottom: isLast ? 'none' : '1px solid #f1f3f5',
        }}>
            <Typography fontFamily="Lato" fontSize="0.9rem" color="#6c757d" sx={{ py: 1.5, pr: 1.5 }}>
                {field.label}
            </Typography>
            <Box />
            <Box sx={{ py: 1.5, pl: 1.5 }}>
                <Typography
                    ref={textRef}
                    component="span"
                    fontFamily="Lato" fontSize="0.9rem" fontWeight={600}
                    color={isNoData ? "#D1D5DB" : "#2c3e50"}
                    onClick={() => !isNoData && (isClamped || expanded) && setExpanded(e => !e)}
                    sx={{
                        display: 'block',
                        wordBreak: 'break-word',
                        ...(!expanded && !isNoData && {
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                        }),
                        ...((isClamped || expanded) && !isNoData && { cursor: 'pointer' }),
                    }}
                >
                    {field.value}
                </Typography>
                {!isNoData && (isClamped || expanded) && (
                    <Typography
                        fontSize="0.75rem" color="#9ca3af" fontFamily="Lato"
                        onClick={() => setExpanded(e => !e)}
                        sx={{ cursor: 'pointer', mt: 0.25, display: 'block' }}
                    >
                        {expanded ? 'Show less' : 'Show more'}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

// ─── Atom: SearchIcon ────────────────────────────────────────────────────────

const SearchIcon: React.FC = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

// ─── Molecule: EmailListItem ──────────────────────────────────────────────────

type SelectedCompany = { dashboard: Dashboard; emailType: string; dealSource: string };

type EmailListItemProps = {
    item: AuditEmail;
    isSelected: boolean;
    selectedDashboard: Dashboard | null;
    onSelectEmail: (item: AuditEmail) => void;
    onSelectDashboard: (d: Dashboard | null, emailType: string, dealSource: string) => void;
};

const EmailListItem: React.FC<EmailListItemProps> = ({
    item, isSelected, selectedDashboard, onSelectEmail, onSelectDashboard,
}) => {
    const classes = useStyles();
    const { emailBody, dashboards } = item;

    const emailTypeLabel = useMemo(
        () => normalizeEmailType(emailBody.type, emailBody.contentClassification),
        [emailBody.type, emailBody.contentClassification],
    );
    const emailTypeColor = useMemo(
        () => getEmailTypeColor(emailBody.type, emailBody.contentClassification),
        [emailBody.type, emailBody.contentClassification],
    );
    // Use the first dashboard's deal source as the sender label
    const dealSource = useMemo(
        () => dashboards[0]?.dealSource?.name || dashboards[0]?.source || "",
        [dashboards],
    );
    const subject = useMemo(
        () => (emailBody.subject || "(No subject)").replace(/^fwd:\s*/i, ""),
        [emailBody.subject],
    );
    const relativeDate = useMemo(
        () => (emailBody.createdAt ? moment(emailBody.createdAt).fromNow() : ""),
        [emailBody.createdAt],
    );
    const companiesLabel = useMemo(
        () => `${dashboards.length} compan${dashboards.length === 1 ? "y" : "ies"}`,
        [dashboards.length],
    );

    const handleClick = useCallback(() => onSelectEmail(item), [item, onSelectEmail]);

    return (<>
        <Box className={classes.timelineItem} onClick={handleClick}>
            <Stack
                className={`${classes.timelineContent} ${isSelected ? classes.timelineContentSelected : ""}`}
                spacing={1}
            >
                {/* Row 1: email type badge + relative date */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    {emailTypeLabel ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Box component="span" sx={{
                                width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                background: emailTypeColor, display: "inline-block",
                            }} />
                            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#374151", fontFamily: "Inter" }}>
                                {emailTypeLabel}
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#9CA3AF", fontFamily: "Inter" }}>
                            No classification
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: "12px", color: "#9ca3af", fontFamily: "Inter", whiteSpace: "nowrap" }}>
                        {relativeDate}
                    </Typography>
                </Stack>

                {/* Row 2: email subject */}
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827", fontFamily: "Inter", lineHeight: 1.4 }}>
                    {subject}
                </Typography>

                {/* Row 4+: companies label then one chip per company, or "No companies extracted" */}
                <Box>
                    {dashboards.length === 0 ? (
                        <Stack direction="row" spacing={0.5} alignItems="center"
                            sx={{ px: 1, py: 0.5, borderRadius: 1, background: "#FEF2F2", width: "fit-content" }}>
                            <WarningAmberIcon sx={{ fontSize: 14, color: "#EF4444" }} />
                            <Typography sx={{ fontSize: "0.78rem", fontWeight: 500, color: "#EF4444", fontFamily: "Inter" }}>
                                No companies extracted
                            </Typography>
                        </Stack>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.07em", mb: 0.5, fontFamily: "Inter" }}>
                                {companiesLabel}
                            </Typography>
                            <Stack spacing={0.5}>
                                {dashboards.map((dashboard) => (
                                    <CompanyChipMolecule
                                        key={dashboard.id}
                                        dashboard={dashboard}
                                        isSelected={selectedDashboard?.id === dashboard.id}
                                        onSelect={(e) => {
                                            e.stopPropagation();
                                            onSelectEmail(item);
                                            const isChipSelected = selectedDashboard?.id === dashboard.id;
                                            onSelectDashboard(
                                                isChipSelected ? null : dashboard,
                                                emailTypeLabel,
                                                dealSource,
                                            );
                                        }}
                                    />
                                ))}
                            </Stack>
                        </>
                    )}
                </Box>
            </Stack>
        </Box>
    </>);
};

// ─── Molecule: CompanyChipMolecule ───────────────────────────────────────────

type CompanyChipMoleculeProps = {
    dashboard: Dashboard;
    isSelected: boolean;
    onSelect: (e: React.MouseEvent) => void;
};

const CompanyChipMolecule: React.FC<CompanyChipMoleculeProps> = ({ dashboard, isSelected, onSelect }) => {
    const classes = useStyles();

    return (
        <button
            className={`${classes.companyChip} ${isSelected ? classes.companyChipActive : ""}`}
            onClick={onSelect}
        >
            <Stack direction="row" alignItems="center" spacing={0.7}>
                <Typography sx={{
                    fontSize: "0.875rem", fontWeight: 600,
                    color: "#065F46",
                    fontFamily: "Inter",
                }}>
                    {dashboard.title}
                </Typography>
            </Stack>
        </button>
    );
};

// ─── Organism: EmailIndexPanel ────────────────────────────────────────────────

type EmailIndexPanelProps = {
    items: AuditEmail[];
    selectedItem: AuditEmail | null;
    selectedDashboard: Dashboard | null;
    onSelectEmail: (item: AuditEmail) => void;
    onSelectDashboard: (d: Dashboard | null, emailType: string, dealSource: string) => void;
};

const DATE_FMT = "MMM D, YYYY";

const EmailIndexPanel: React.FC<EmailIndexPanelProps> = ({
    items, selectedItem, selectedDashboard, onSelectEmail, onSelectDashboard,
}) => {
    const classes = useStyles();

    const itemsByDate = useMemo(() => {
        const grouped: Record<string, AuditEmail[]> = {};
        items.forEach(item => {
            const raw = item.emailBody.createdAt ?? item.dashboards[0]?.createdAt;
            const key = raw ? moment(raw).format(DATE_FMT) : "Unknown";
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(item);
        });
        return grouped;
    }, [items]);

    const sortedDates = useMemo(() =>
        Object.keys(itemsByDate).sort((a, b) => {
            const ma = moment(a, DATE_FMT, true).isValid() ? moment(a, DATE_FMT) : moment(a);
            const mb = moment(b, DATE_FMT, true).isValid() ? moment(b, DATE_FMT) : moment(b);
            return mb.valueOf() - ma.valueOf();
        }),
        [itemsByDate]);

    return (
        <Box className={classes.leftPanel}>
            {/* Header */}
            <Box className={classes.leftPanelHeader}>
                <Box>
                    <Typography className={classes.leftPanelLabel}>Email Timeline</Typography>
                    <Typography className={classes.leftPanelCount}>
                        {items.length} email{items.length !== 1 ? "s" : ""}
                    </Typography>
                </Box>
            </Box>

            {/* Timeline list */}
            <Box className={classes.leftPanelList}>
                {items.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography sx={{ fontSize: "0.95rem", color: "#9CA3AF", fontFamily: "Inter" }}>No emails found</Typography>
                    </Box>
                ) : (
                    sortedDates.map((date, idx) => {
                        const group = itemsByDate[date];
                        const firstDate = moment(group[0].emailBody.createdAt ?? group[0].dashboards[0]?.createdAt);
                        return (
                            <Box key={idx} className={classes.dateGroup}>
                                {/* Date header with dot */}
                                <Box className={classes.dateHeader}>
                                    <Stack direction="row" alignItems="baseline" spacing={1}>
                                        <Typography className={classes.dateText}>{date}</Typography>
                                        <Typography className={classes.dateRelative}>• {firstDate.fromNow()}</Typography>
                                    </Stack>
                                    <Typography className={classes.dateBadge}>
                                        {group.length} email{group.length > 1 ? "s" : ""}
                                    </Typography>
                                </Box>
                                {/* Items */}
                                {group.map((item) => (
                                    <EmailListItem
                                        key={item.emailBody.id ?? item.emailBody.subject ?? item.dashboards.map(d => d.id).join("-")}
                                        item={item}
                                        isSelected={selectedItem?.emailBody.id === item.emailBody.id && selectedItem?.emailBody.subject === item.emailBody.subject}
                                        selectedDashboard={selectedDashboard}
                                        onSelectEmail={onSelectEmail}
                                        onSelectDashboard={onSelectDashboard}
                                    />
                                ))}
                            </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );
};

// ─── Organism: EmailDetailPanel ───────────────────────────────────────────────

type EmailDetailPanelProps = {
    item: AuditEmail | null;
    selectedDashboard: Dashboard | null;
    selectedCompanyMeta: SelectedCompany | null;
    onSelectDashboard: (d: Dashboard | null, emailType: string, dealSource: string) => void;
};

// One extracted company card is ~72px tall; the label row is ~36px
const FOOTER_MIN = 36 + 72 + 8;   // label + 1 card + padding
const FOOTER_MAX_VH = 0.5;         // 50% of viewport height

const EmailDetailPanel: React.FC<EmailDetailPanelProps> = ({
    item, selectedDashboard, onSelectDashboard,
}) => {
    const classes = useStyles();
    const { fileStructure } = useContext(FileStructureContext);
    const scrollRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);
    const [footerHeight, setFooterHeight] = useState(220);
    const [selectedFile, setSelectedFile] = useState<Attachment | null>(null);

    const attachedLinks = useMemo<string[]>(() => getAttachedLinks(item?.emailBody), [item?.emailBody]);

    const attachedFiles = useMemo<CompanyFile[]>(() => {
        if (!item) return [];
        const { emailBody, dashboards } = item;
        const dashboardIds = new Set(dashboards.map(d => d.id));
        const attachmentNames = getAttachmentNameSet(emailBody);
        const seen = new Set<string>();
        const result: CompanyFile[] = [];
        fileStructure.forEach(f => {
            if (f.isDirectory || !f.dashboardId || !dashboardIds.has(f.dashboardId)) return;
            const nameMatched = attachmentNames.size > 0 && attachmentNames.has(normalizeAttachmentToken(f.name));
            const timeDiff = moment(f.createdAt).diff(moment(emailBody.createdAt), 'minutes', true);
            const timeMatched = timeDiff >= 0 && timeDiff <= 5;
            if ((nameMatched || timeMatched) && !seen.has(f.id)) {
                seen.add(f.id);
                result.push(f);
            }
        });
        return result;
    }, [item, fileStructure]);

    const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const startY = e.clientY;
        const startHeight = footerHeight;
        const panelEl = panelRef.current;

        const onMouseMove = (ev: MouseEvent) => {
            const delta = startY - ev.clientY; // dragging up = larger footer
            const maxH = panelEl
                ? Math.min(panelEl.clientHeight * FOOTER_MAX_VH, window.innerHeight * FOOTER_MAX_VH)
                : window.innerHeight * FOOTER_MAX_VH;
            const next = Math.min(maxH, Math.max(FOOTER_MIN, startHeight + delta));
            setFooterHeight(next);
        };
        const onMouseUp = () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    }, [footerHeight]);

    const bodyText = useMemo(() => {
        if (!item?.emailBody.content) return null;
        return decode(item.emailBody.content)
            .toString()
            .replace(/\r\n/g, '\n')
            .split('\n')
            .map(line => {
                const match = line.match(/^([>]+)\s?(.*)$/);
                if (!match) return line;
                const depth = match[1]?.length;
                const content = match[2]?.trim();
                if (!content) return '';
                return ' '.repeat(depth) + content;
            })
            .join('\n');
    }, [item?.emailBody.content]);

    // Detect overflow whenever item or bodyText changes
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const check = () => {
            const overflow = el.scrollHeight > el.clientHeight + 2;
            setIsOverflowing(overflow);
            setIsScrolledToBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
            setIsScrolledFromTop(el.scrollTop > 8);
        };
        check();
        el.addEventListener('scroll', check);
        let rafId: number;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(check);
        });
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', check);
            cancelAnimationFrame(rafId);
            ro.disconnect();
        };
    }, [item]);

    const showBottomFade = isOverflowing && !isScrolledToBottom;
    const showTopFade = isScrolledFromTop;

    if (!item) {
        return (
            <Stack className={classes.centerPanel}>
                {/* Viewer header – no email selected */}
                <Stack direction="row" className={classes.centerPanelHeader} alignItems="center" spacing={1.5}>
                    <MailIcon sx={{ color: "#9ca3af", fontSize: 18, flexShrink: 0 }} />
                    <Typography className={classes.centerPanelHeaderTitle}>
                        Select an email to view
                    </Typography>
                </Stack>
                <Stack className={classes.centerPanelEmpty}>
                    <MailIcon sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }} />
                    <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#6b7280", mb: 1, fontFamily: "Inter" }}>
                        No email selected
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#9ca3af", fontFamily: "Inter" }}>
                        Click on an email from the timeline to view
                    </Typography>
                </Stack>
            </Stack>
        );
    }

    const { dashboards, emailBody } = item;
    const emailTypeLabel = normalizeEmailType(emailBody.type, emailBody.contentClassification);
    const dealSource = dashboards[0]?.dealSource?.name || dashboards[0]?.source || "";
    const companiesExtractedLabel = `${dashboards.length} compan${dashboards.length === 1 ? "y" : "ies"} extracted`;

    return (
        <Stack ref={panelRef} className={classes.centerPanel}>
            {/* Viewer header */}
            <Stack direction="row" className={classes.centerPanelHeader} alignItems="center" spacing={1.5}>
                <MailIcon sx={{ color: "#9ca3af", fontSize: 18, flexShrink: 0 }} />
                <Typography className={classes.centerPanelHeaderTitle}>
                    {emailBody.subject || "Select an email to view"}
                </Typography>
            </Stack>

            {/* Scrollable area – email card only */}
            <Box className={classes.centerPanelContainer}>
                <Box ref={scrollRef} className={classes.centerPanelScrollArea}>
                    {/* Email card */}
                    <Box className={classes.centerPanelFrame}>
                        <Box className={classes.emailHeaderInfo}>
                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
                                <Typography className={classes.emailSubject}>
                                    {emailBody.subject || "(No subject)"}
                                </Typography>
                                {emailTypeLabel ? (
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0, mt: 0.5 }}>
                                        <Box component="span" sx={{
                                            width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                            background: getEmailTypeColor(emailBody.type, emailBody.contentClassification),
                                            display: "inline-block",
                                        }} />
                                        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#374151", fontFamily: "Inter" }}>
                                            {emailTypeLabel}
                                        </Typography>
                                    </Stack>
                                ) : (
                                    <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#9CA3AF", fontFamily: "Inter", flexShrink: 0, mt: 0.5 }}>
                                        No classification
                                    </Typography>
                                )}
                            </Stack>
                            <Stack className={classes.emailMeta}>
                                <Stack direction="row" className={classes.emailMetaRow}>
                                    <Typography className={classes.emailMetaLabel}>From:</Typography>
                                    <Typography className={classes.emailMetaValue}>{emailBody.from}</Typography>
                                </Stack>
                                <Stack direction="row" className={classes.emailMetaRow}>
                                    <Typography className={classes.emailMetaLabel}>Date:</Typography>
                                    <Typography className={classes.emailMetaValue}>
                                        {emailBody.createdAt ? moment(emailBody.createdAt).format("MMM D, YYYY") : ""}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>

                        {/* Metadata bar – edge-to-edge */}
                        {(attachedFiles.length > 0 || attachedLinks.length > 0) ? (
                            <Box sx={{
                                display: "flex", alignItems: "center", gap: "6px",
                                background: "#f9fafb", borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb",
                                mx: -4, px: 4, py: "10px", mb: 3,
                                fontSize: "14px", color: "#6b7280", fontFamily: "Inter",
                                flexWrap: "wrap",
                            }}>
                                <Stack sx={{ width: "100%", mt: 0.5 }}>
                                    <EmailDocumentsSection
                                        files={attachedFiles}
                                        links={attachedLinks}
                                        allFiles={attachedFiles}
                                        onFileClick={(file) => setSelectedFile({
                                            ...file,
                                            isLink: false,
                                            filePath: file.path,
                                            fileType: file.name.toLowerCase().split('.').pop() || '',
                                        } as Attachment)}
                                    />
                                </Stack>
                            </Box>
                        ) : (
                            <Box sx={{ borderTop: "1px solid #e5e7eb", mx: -4, mb: 3 }} />
                        )}

                        <Typography className={classes.emailBodyText}>
                            {bodyText || "(No content)"}
                        </Typography>
                    </Box>
                </Box>

                {selectedFile && (
                    <Dialog open={!!selectedFile} onClose={() => setSelectedFile(null)} maxWidth={false}
                        PaperProps={{ sx: { width: '85vw', maxWidth: '1100px', height: '88vh', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}>
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 2, py: 1, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                            <IconButton onClick={() => setSelectedFile(null)} size="small"
                                sx={{ borderRadius: '8px', backgroundColor: '#f3f4f6', color: '#6b7280', '&:hover': { backgroundColor: '#e5e7eb' } }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                            <FileViewerOrganism selectedAttachment={selectedFile} />
                        </Box>
                    </Dialog>
                )}

                {/* Fade overlays */}
                {showTopFade && <Box className={classes.centerPanelFrameFadeTop} />}
                {showBottomFade && <Box className={classes.centerPanelFrameFade} />}
            </Box>

            {/* Companies Extracted – resizable footer */}
            <Box className={classes.centerPanelCompaniesFooter} sx={{ height: footerHeight }}>
                {/* Drag handle */}
                <Box className={classes.resizeHandle} onMouseDown={handleResizeMouseDown}>
                    <Box className={classes.resizeHandleDots} />
                </Box>
                {/* Fixed label row */}
                <Box sx={{ padding: "4px 24px 0", flexShrink: 0 }}>
                    {dashboards.length === 0 ? (
                        <Stack direction="row" spacing={0.75} alignItems="center"
                            sx={{ px: 1.5, py: 0.75, borderRadius: 1, background: "#FEF2F2", width: "fit-content" }}>
                            <WarningAmberIcon sx={{ fontSize: 16, color: "#EF4444" }} />
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#EF4444", fontFamily: "Inter" }}>
                                No companies extracted
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography className={classes.extractedLabel}>{companiesExtractedLabel}</Typography>
                    )}
                </Box>
                {/* Scrollable company cards */}
                <Box className={classes.centerPanelCompaniesFooterInner}>
                    <Box sx={{ maxWidth: 800, margin: "0 auto" }}>
                        <Stack spacing={1}>
                            {dashboards.map((dashboard) => {
                                const isSelected = selectedDashboard?.id === dashboard.id;
                                return (
                                    <Box
                                        key={dashboard.id}
                                        className={`${classes.extractedCard} ${isSelected ? classes.extractedCardActive : ""}`}
                                        onClick={() => onSelectDashboard(
                                            isSelected ? null : dashboard,
                                            emailTypeLabel,
                                            dealSource,
                                        )}
                                        sx={{ mb: 0, "&:hover": { boxShadow: isSelected ? "none" : "0 2px 8px rgba(0,0,0,0.07)" } }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <Box>
                                                <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: isSelected ? "#065F46" : "#111827", fontFamily: "Inter" }}>
                                                    {dashboard.title}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                            {dashboard.status && <StatusBadgeAtom label={dashboard.status} />}
                                            {dashboard.investmentStage && <StageBadgeAtom stage={dashboard.investmentStage} />}
                                            <Typography sx={{ fontSize: "0.95rem", color: isSelected ? "#0D9488" : "#9CA3AF", fontWeight: 600 }}>
                                                {isSelected ? "▸" : "›"}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Stack>
    );
};

// ─── Organism: CompanyDetailPanel ────────────────────────────────────────────

type CompanyDetailPanelProps = {
    dashboard: Dashboard | null;
    selectedItem: AuditEmail | null;
    emailType: string;
    dealSource: string;
    onClose: () => void;
};

const CompanyDetailPanel: React.FC<CompanyDetailPanelProps> = ({
    dashboard, selectedItem, emailType, dealSource, onClose,
}) => {
    const classes = useStyles();
    const history = useHistory();
    const { mappedOverviewQueries, dashboardsQueries } = useContext(DashboardsContext);

    const handleOpenRecord = useCallback(() => {
        if (dashboard) history.push(`/dashboards/${dashboard.id}`);
    }, [dashboard, history]);

    // All derived data is memoized before the early return so hooks order is stable
    const { fileStructure } = useContext(FileStructureContext);
    const [selectedFile, setSelectedFile] = useState<Attachment | null>(null);
    const { tagGroups, customFields: groupCustomFields } = useContext(GroupSettingsContext);
    const sectorsTagGroup = useMemo(() => tagGroups?.find(({ title }) => title.toLowerCase() === 'sectors'), [tagGroups]);

    const lookUpTagsSelection = useCallback((groupTitle: string = 'sectors') =>
        dashboard?.tagGroups?.find(({ title }) => title.toLowerCase() === groupTitle.toLowerCase())?.tags || []
        , [dashboard]);

    const handleSelectTags = useCallback(async (selections: string[]) => {
        const title = 'Sectors';
        if (!dashboard) return;
        const lookUpTagGroup = dashboard.tagGroups?.find(g => g.title.toLowerCase() === title.toLowerCase());
        if (!ObjectUtils.equalObjects(lookUpTagGroup?.tags, selections)) {
            setUpdating(true);
            await updateDashboardFunc(produce(dashboard, (draft) => {
                if (!draft.tagGroups?.find(g => g.title.toLowerCase() === title.toLowerCase())) {
                    draft.tagGroups = [...(draft.tagGroups || []), { title, tags: selections }]
                        .map(({ title, tags }) => ({ title, tags }));
                } else {
                    const idx = draft.tagGroups.findIndex(g => g.title.toLowerCase() === title.toLowerCase());
                    draft.tagGroups[idx].tags = selections;
                    draft.tagGroups = draft.tagGroups.map(({ title, tags }) => ({ title, tags }));
                }
            })).then(() => setUpdating(false));
        }
    }, [dashboard]);

    // Edit popover state (mirrors CompanyDetailsCard)
    const [editQueryProps, setEditField] = useState<{
        anchor: HTMLElement | null;
        field: string;
        value: string;
        queryTitle: string;
    }>({ anchor: null, field: '', value: '', queryTitle: '' });
    const [updating, setUpdating] = useState(false);

    const getQuery = useCallback((title: string): DashboardQuery | undefined =>
        dashboardsQueries.find(q => q.dashboardId === dashboard?.id && q.title === title)
        , [dashboardsQueries, dashboard?.id]);

    const handleTextEdit = useCallback((field: string, queryTitle: string, value: string) => {
        setEditField({ anchor: document.activeElement as HTMLElement, field, value, queryTitle });
    }, []);

    const handleResetQueryUpdate = useCallback(() => {
        setEditField({ anchor: null, field: '', value: '', queryTitle: '' });
    }, []);

    const handleQueryUpdate = useCallback(async (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!dashboard || !editQueryProps.queryTitle) return;
        setUpdating(true);
        const existing = getQuery(editQueryProps.queryTitle);
        await addDashboardQueryFunc(
            dashboard.id,
            editQueryProps.queryTitle,
            dashboard.group,
            existing?.query ?? editQueryProps.queryTitle,
            JSON.stringify({ answer: editQueryProps.value, type: AnswerQuestionResponseType.TEXT }),
            existing?.history,
            existing?.order ?? 0,
            existing?.manualOverride ?? true,
            (existing?.source as DashboardQuerySource) ?? DashboardQuerySource.OPENAI,
        );
        setUpdating(false);
        handleResetQueryUpdate();
    }, [dashboard, editQueryProps, getQuery, handleResetQueryUpdate]);

    const tags = useMemo(() => dashboard?.tags ?? [], [dashboard?.tags]);
    const customFieldRows = useMemo<{ label: string; value: string }[]>(() => {
        if (!groupCustomFields?.length) return [];

        return groupCustomFields.map((field) => {
            const label = (field.name || "").trim();
            const dashboardField = dashboard?.customFields?.find(
                f => (f.name || "").trim().toLowerCase() === label.toLowerCase()
            );
            const value = dashboardField
                ? Array.isArray(dashboardField.value)
                    ? dashboardField.value.map((v: any) => `${v ?? ""}`.trim()).filter(Boolean).join(", ")
                    : typeof dashboardField.value === "string"
                        ? dashboardField.value.trim()
                        : ""
                : "";
            return { label, value: value || "None" };
        });
    }, [groupCustomFields, dashboard?.customFields]);
    const companyDetails = useMemo(() => {
        if (!dashboard) return "";

        const asText = (value: unknown): string => {
            if (typeof value === "string") return value.trim();
            if (Array.isArray(value)) {
                return value
                    .map(v => (typeof v === "string" ? v.trim() : ""))
                    .filter(Boolean)
                    .join(" ");
            }
            return "";
        };

        const descriptionFromOverview = asText(mappedOverviewQueries.get(`${dashboard.id}:Description`));
        const digestFromOverview = asText(mappedOverviewQueries.get(`${dashboard.id}:Digest`));

        const customSummaryField = (dashboard.customFields ?? []).find((field) => {
            const name = (field.name || "").toLowerCase();
            return name.includes("deal summary") || name.includes("description") || name.includes("summary");
        });
        const descriptionFromCustomField = customSummaryField
            ? asText(customSummaryField.value)
            : "";

        return descriptionFromOverview || digestFromOverview || descriptionFromCustomField || "";
    }, [dashboard, mappedOverviewQueries]);
    const attachedLinks = useMemo<string[]>(() => getAttachedLinks(selectedItem?.emailBody), [selectedItem?.emailBody]);

    const dashboardFiles = useMemo<CompanyFile[]>(() => {
        if (!selectedItem || !dashboard) return [];
        const { emailBody } = selectedItem;
        const attachmentNames = getAttachmentNameSet(emailBody);
        const seen = new Set<string>();
        const result: CompanyFile[] = [];
        fileStructure.forEach(f => {
            if (f.isDirectory || f.dashboardId !== dashboard.id) return;
            const nameMatched = attachmentNames.size > 0 && attachmentNames.has(normalizeAttachmentToken(f.name));
            const timeDiff = moment(f.createdAt).diff(moment(emailBody.createdAt), 'minutes', true);
            const timeMatched = timeDiff >= 0 && timeDiff <= 5;
            if ((nameMatched || timeMatched) && !seen.has(f.id)) {
                seen.add(f.id);
                result.push(f);
            }
        });
        return result;
    }, [selectedItem, dashboard, fileStructure]);


    const keyPeople = useMemo<any[]>(() => {
        if (!dashboard) return [];
        const val = mappedOverviewQueries.get(`${dashboard.id}:Key People`);
        return Array.isArray(val) ? val : [];
    }, [dashboard, mappedOverviewQueries]);

    const getRoleRank = useCallback((role: string): number => {
        const roleUpper = role?.toUpperCase() || '';

        if (roleUpper.includes('FOUNDER') || roleUpper.includes('CEO')) return 1;
        if (roleUpper.includes('PRESIDENT') && !roleUpper.includes('VICE')) return 2;
        if (roleUpper.includes('CHIEF') ||
            (roleUpper.includes('OFFICER') && (roleUpper.includes('CFO') || roleUpper.includes('COO') || roleUpper.includes('CTO') || roleUpper.includes('CMO') || roleUpper.includes('CPO') || roleUpper.includes('CRO') || roleUpper.includes('CHRO') || roleUpper.includes('CDO') || roleUpper.includes('CIO') || roleUpper.includes('CSO') || roleUpper.includes('CCO'))) ||
            /^C[A-Z]O\b/.test(roleUpper)) return 3;
        if ((roleUpper.includes('EXECUTIVE') && roleUpper.includes('VP')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VP')) ||
            (roleUpper.includes('EXECUTIVE') && roleUpper.includes('VICE PRESIDENT')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VICE PRESIDENT'))) return 4;
        if (roleUpper.includes('VICE PRESIDENT') || roleUpper.includes('VP')) return 5;
        if (roleUpper.includes('HEAD OF') || roleUpper.includes('DIRECTOR')) return 6;
        if (roleUpper.includes('SENIOR')) return 7;
        if (roleUpper.includes('MANAGER') || roleUpper.includes('LEAD') || roleUpper.includes('SPECIALIST') ||
            roleUpper.includes('ANALYST') || roleUpper.includes('COORDINATOR') || roleUpper.includes('JUNIOR')) return 8;
        if (roleUpper.includes('SECRETARY') || roleUpper.includes('ASSISTANT') || roleUpper.includes('CLERK') ||
            roleUpper.includes('ADMIN') || roleUpper.includes('SUPPORT')) return 9;

        return 10;
    }, []);

    const founders = useMemo<any[]>(() => {
        if (!Array.isArray(keyPeople)) return keyPeople as unknown as any[];
        return [...keyPeople].sort((a: any, b: any) => {
            const rankA = getRoleRank(a.role);
            const rankB = getRoleRank(b.role);
            if (rankA !== rankB) return rankA - rankB;
            if (a.role < b.role) return -1;
            if (a.role > b.role) return 1;
            return 0;
        });
    }, [getRoleRank, keyPeople]);

    const dataRows = useMemo<[string, string][]>(() => {
        if (!dashboard) return [];
        const q = (title: string): string => {
            const val = mappedOverviewQueries.get(`${dashboard.id}:${title}`);
            return (!val || typeof val !== "string") ? "" : val;
        };
        const row = (label: string, val: string | null | undefined): [string, string] =>
            [label, val || "No data"];
        return [
            row("Founded", q("Founded")),
            row("Employees", q("Headcount")),
            row("Location", q("Location")),
            row("Website", q("Website")),
        ];
    }, [dashboard, mappedOverviewQueries]);

    const tagGroupRows = useMemo<{ label: string; tags: string[] }[]>(() => {
        if (!dashboard?.tagGroups?.length) return [];
        return dashboard.tagGroups
            .filter(g => g.tags?.length > 0)
            .map(g => ({ label: g.title, tags: g.tags }));
    }, [dashboard?.tagGroups]);

    if (!dashboard) {
        return (
            <Box className={classes.rightPanelEmpty}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: "#9CA3AF", fontFamily: "Inter" }}>
                    Select a company
                </Typography>
            </Box>
        );
    }

    return (
        <Box className={classes.rightPanel}>
            {/* Header */}
            <Box className={classes.rightPanelHeader}>
                <Stack direction="row" alignItems="center" spacing={1.2}>
                    <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: "#111827", lineHeight: 1.2, fontFamily: "Inter" }}>
                        {dashboard.title}
                    </Typography>
                </Stack>
                <Tooltip title="Close">
                    <Box
                        component="button"
                        onClick={onClose}
                        sx={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14, p: "4px", lineHeight: 1, borderRadius: 1, "&:hover": { color: "#374151" } }}
                    >
                        ✕
                    </Box>
                </Tooltip>
            </Box>

            {/* Scrollable content */}
            <Box className={classes.rightPanelContent}>
                {/* Company details summary */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: 1 }}>

                    <Box mb={2}>
                        <Typography fontSize={16} fontWeight={600} color="#2c3e50">
                            {'Company Details'}
                        </Typography>
                    </Box>

                    {/* Description block */}
                    <Stack direction="column" spacing={1} alignItems="flex-start" sx={{
                        pb: 1.5, borderBottom: '1px solid #f1f3f5',
                        '&:hover': { bgcolor: '#fafbfc', mx: -1.5, px: 1.5, borderRadius: 0.75, '& button': { opacity: 1 } },
                    }}>
                        <Stack direction="row" spacing="4px" alignItems="flex-start" justifyContent="space-between" width="100%">
                            <Typography fontFamily="Lato" fontSize="0.9rem" color="#2c3e50" fontWeight={500}>
                                {companyDetails || <span style={{ color: "#9CA3AF" }}>No company details available.</span>}
                            </Typography>
                            <IconButton size="small" sx={{ opacity: 0, transition: 'opacity 0.2s', flexShrink: 0 }}
                                onClick={() => handleTextEdit('Description', 'Description', companyDetails || '')} disableRipple>
                                <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Stack>
                    </Stack>
                    {/* Inline rows */}
                    {dataRows.map(([label, val], i) => {
                        const isWebsite = label === "Website" && val !== "No data";
                        const href = isWebsite ? (val.startsWith("http") ? val : `https://${val}`) : undefined;
                        const displayUrl = isWebsite ? val.replace(/^https?:\/\//i, "").replace(/\/.*$/, "") : val;
                        const isTagged = label === "Referred By" || label === "Account Source";
                        const showTagGroupsAfter = label === "Website";
                        const isNoData = val === "No data";
                        const queryTitleMap: Record<string, string> = {
                            "Founded": "Founded", "Employees": "Headcount",
                            "Location": "Location", "Website": "Website",
                        };
                        const queryTitle = queryTitleMap[label];
                        const editable = !!queryTitle;

                        return (
                            <React.Fragment key={i}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{
                                    py: 1.5,
                                    borderBottom: (i === dataRows.length - 1) ? 'none' : '1px solid #f1f3f5',
                                    ...(editable && {
                                        '&:hover': { bgcolor: '#fafbfc', mx: -1.5, px: 1.5, borderRadius: 0.75, '& button': { opacity: 1 } },
                                    }),
                                }}>
                                    <Typography fontFamily="Lato" fontSize="0.9rem" color="#6c757d" minWidth={70}>
                                        {label}
                                    </Typography>
                                    <Stack direction="row" alignItems="center">
                                        {isWebsite ? (
                                            <Link href={href} target="_blank" rel="noopener noreferrer"
                                                fontFamily="Lato" fontSize="0.9rem" fontWeight={600} color="#0D9488"
                                                sx={{ textAlign: "right", maxWidth: 168, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {displayUrl}
                                            </Link>
                                        ) : isTagged ? (
                                            val && val !== "No data" ? (
                                                <Chip label={val} sx={{
                                                    height: 30, borderRadius: "8px",
                                                    backgroundColor: '#F1F3F5', color: '#4A5056',
                                                    fontFamily: 'Lato', fontWeight: 600, fontSize: '0.8rem',
                                                }} />
                                            ) : (
                                                <Typography fontFamily="Lato" fontSize="0.9rem" color="#D1D5DB" fontWeight={600}>—</Typography>
                                            )
                                        ) : (
                                            <Typography fontFamily="Lato" fontSize="0.9rem" color={isNoData ? "#D1D5DB" : "#2c3e50"} fontWeight={600} textAlign="right">
                                                {val}
                                            </Typography>
                                        )}
                                        {editable && (
                                            <IconButton size="small" sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                                                onClick={() => handleTextEdit(label, queryTitle, isNoData ? '' : val)} disableRipple>
                                                <EditIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Stack>
                                {showTagGroupsAfter && tagGroupRows.map((group, gi) => {
                                    const isSectors = group.label.toLowerCase() === 'sectors';
                                    return (
                                        <Stack key={group.label} direction="row" justifyContent="space-between" sx={{ py: 1.5, borderBottom: gi === tagGroupRows.length - 1 ? 'none' : '1px solid #f1f3f5', alignItems: 'flex-start' }}>
                                            <Typography fontFamily="Lato" fontSize="0.9rem" color="#6c757d" minWidth={70} pt="3px">
                                                {group.label}
                                            </Typography>
                                            {isSectors ? (
                                                <Box sx={{ "& > div > div": { justifyContent: 'flex-end' } }}>
                                                    <MultiSelector
                                                        mainOptions={sectorsTagGroup?.tags || []}
                                                        selections={lookUpTagsSelection()}
                                                        onSelection={(selections) => handleSelectTags(selections)}
                                                        loading={updating}
                                                        limit={sectorsTagGroup?.tagsPerDashboard || 3}
                                                        withSearch
                                                        fullSpan />
                                                </Box>
                                            ) : (
                                                <Stack direction="row" flexWrap="wrap" gap={0.5} justifyContent="flex-end" sx={{ maxWidth: 200 }}>
                                                    {group.tags.map((tag, ti) => (
                                                        <Chip key={ti} label={tag} sx={{
                                                            height: 30, borderRadius: "8px",
                                                            backgroundColor: '#F1F3F5', color: '#4A5056',
                                                            fontFamily: 'Lato', fontWeight: 600, fontSize: '0.8rem',
                                                        }} />
                                                    ))}
                                                </Stack>
                                            )}
                                        </Stack>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </Paper>

                {/* Edit Popover */}
                <Popover
                    open={Boolean(editQueryProps.anchor)}
                    anchorEl={editQueryProps.anchor}
                    onClose={handleResetQueryUpdate}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                    <Box sx={{ width: 350, p: 2, borderRadius: 8 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography fontSize={14} fontWeight={600}>
                                {`Edit ${editQueryProps.field}`}
                            </Typography>
                            <IconButton size="small" onClick={handleResetQueryUpdate}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Stack>
                        <TextField
                            value={editQueryProps.value}
                            rows={editQueryProps.field === 'Description' ? 5 : undefined}
                            onChange={(e) => setEditField(prev => ({ ...prev, value: e.target.value }))}
                            fullWidth multiline />
                        <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
                            <Button size="small" onClick={handleResetQueryUpdate}>Cancel</Button>
                            <Button size="small" variant="contained" onClick={handleQueryUpdate} disabled={updating}>
                                Save
                            </Button>
                        </Stack>
                    </Box>
                </Popover>

                {/* Deal Source */}
                {(dashboard?.dealSource?.name || dealSource) && (<>
                    <Box sx={{ borderTop: "1px solid #F3F4F6" }} />
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 1 }}>
                        <Typography fontSize={16} fontWeight={600} color="#2c3e50" mb={2}>
                            {'Deal Source'}
                        </Typography>
                        <DealSourceRow
                            name={dashboard?.dealSource?.name || dealSource}
                            email={dashboard?.dealSource?.email || undefined}
                        />
                    </Paper>
                </>)}

                {/* Team */}
                {(<>
                    <Box sx={{ borderTop: "1px solid #F3F4F6" }} />
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 1 }}>
                        <Typography fontSize={16} fontWeight={600} color="#2c3e50" mb={2}>
                            {'Team'}
                        </Typography>
                        <Stack spacing={2}>
                            {founders.map((p: any, i: number) => {
                                const name = (p.title || "").split("|")[0].split("-")[0].trim();
                                const initials = name ? name.split(' ').slice(0, 2).map((w: string) => w.charAt(0).toUpperCase()).join('') : '';
                                return (
                                    <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                                        <Avatar sx={{
                                            width: 48, height: 48,
                                            background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)',
                                            fontWeight: 600, fontSize: 16,
                                        }}>
                                            {initials}
                                        </Avatar>
                                        <Box flex={1}>
                                            <Typography fontSize={15} fontWeight={600} color="#2c3e50">{name}</Typography>
                                            {!!p.role && (
                                                <Typography fontSize={13} sx={{ mt: 0.25, display: 'block', color: '#6B7280' }}>{p.role}</Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Paper>
                </>)}

                {/* Documents */}
                {(dashboardFiles.length > 0 || attachedLinks.length > 0) && (<>
                    <Box sx={{ borderTop: "1px solid #F3F4F6" }} />
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 1 }}>
                        <AuditDocumentsSection
                            files={dashboardFiles}
                            links={attachedLinks}
                            allFiles={dashboardFiles}
                            onFileClick={(file) => setSelectedFile({
                                ...file,
                                isLink: false,
                                filePath: file.path,
                                fileType: file.name.toLowerCase().split('.').pop() || '',
                            } as Attachment)}
                        />
                    </Paper>
                </>)}

                {/* Custom Tags */}
                {tags.length > 0 && (
                    <Box className={classes.rightPanelSection}>
                        <Typography className={classes.rightPanelSectionLabel}>Tags</Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.6}>
                            {tags.map((t, i) => <TagChipAtom key={i} label={t} />)}
                        </Stack>
                    </Box>
                )}

                {/* Custom Fields */}
                {(<>
                    <Box sx={{ borderTop: "1px solid #F3F4F6" }} />
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 1 }}>
                        <Box mb={2}>
                            <Typography fontSize={16} fontWeight={600} color="#2c3e50">
                                {'Custom Fields'}
                            </Typography>
                        </Box>
                        {customFieldRows.map((field, i) => (
                            <CustomFieldRow
                                key={`${field.label}-${i}`}
                                field={field}
                                isLast={i === customFieldRows.length - 1}
                            />
                        ))}
                    </Paper>
                </>)}

            </Box>

            {selectedFile && (
                <Dialog open={!!selectedFile} onClose={() => setSelectedFile(null)} maxWidth={false}
                    PaperProps={{ sx: { width: '85vw', maxWidth: '1100px', height: '88vh', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}>
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 2, py: 1, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                        <IconButton onClick={() => setSelectedFile(null)} size="small"
                            sx={{ borderRadius: '8px', backgroundColor: '#f3f4f6', color: '#6b7280', '&:hover': { backgroundColor: '#e5e7eb' } }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <FileViewerOrganism selectedAttachment={selectedFile} />
                    </Box>
                </Dialog>
            )}

            {/* CTA */}
            <Box className={classes.rightPanelCta}>
                <Button
                    onClick={handleOpenRecord}
                    fullWidth
                    disableRipple
                    sx={{
                        background: "linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)", color: "#fff", borderRadius: 2,
                        backgroundSize: "200% 100%",
                        backgroundPosition: "0% 50%",
                        fontSize: "0.95rem", fontWeight: 600, textTransform: "none",
                        fontFamily: "Inter", py: "9px",
                        boxShadow: "none",
                        transition: "background-position 280ms ease",
                        "&:hover": { backgroundPosition: "100% 50%" },
                    }}
                >
                    Open Company →
                </Button>
            </Box>
        </Box>
    );
};

// ─── Page reducer ─────────────────────────────────────────────────────────────

type SelectionState = {
    selectedItem: AuditEmail | null;
    selectedDashboard: Dashboard | null;
    selectedCompanyMeta: SelectedCompany | null;
};

type SelectionAction =
    | { type: "SELECT_EMAIL"; item: AuditEmail }
    | { type: "SELECT_DASHBOARD"; dashboard: Dashboard | null; emailType: string; dealSource: string };

const selectionInitial: SelectionState = {
    selectedItem: null,
    selectedDashboard: null,
    selectedCompanyMeta: null,
};

function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
    switch (action.type) {
        case "SELECT_EMAIL":
            return { selectedItem: action.item, selectedDashboard: null, selectedCompanyMeta: null };
        case "SELECT_DASHBOARD":
            return {
                ...state,
                selectedDashboard: action.dashboard,
                selectedCompanyMeta: action.dashboard
                    ? { dashboard: action.dashboard, emailType: action.emailType, dealSource: action.dealSource }
                    : null,
            };
        default:
            return state;
    }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const AuditPage: React.FC = () => {
    const classes = useStyles();
    const { dashboards, dashboardsLoaded } = useContext(DashboardsContext);
    const { unassignedEmails } = useContext(InboxContext);

    const [{ selectedItem, selectedDashboard, selectedCompanyMeta }, dispatch] = React.useReducer(
        selectionReducer,
        selectionInitial,
    );
    const [activeFilter, setActiveFilter] = useState<FilterTab>("All");
    const [searchQuery, setSearchQuery] = useState("");

    // Group dashboards by emailBody id (or subject as fallback) → one card per email
    const auditEmails = useMemo<AuditEmail[]>(() => {
        // emailKey → { emailBody, dashboards[] }
        const map = new Map<string, AuditEmail>();
        for (const dashboard of dashboards) {
            if (!dashboard.emailBodies?.length) continue;
            for (const emailBody of dashboard.emailBodies) {
                const key = emailBody.id ?? emailBody.subject ?? "__no_key__";
                if (!map.has(key)) {
                    map.set(key, { emailBody, dashboards: [] });
                }
                map.get(key)!.dashboards.push(dashboard);
            }
        }

        // Add unassigned emails (not yet linked to any dashboard)
        for (const content of unassignedEmails) {
            const key = content.id || `${content.subject}__${content.createdAt}` || `__unassigned__${Math.random()}`;
            if (map.has(key)) continue;
            const emailBody: EmailBody = {
                id: content.id,
                subject: content.subject,
                from: content.from,
                createdAt: content.createdAt,
                attachments: content.attachments ?? null,
                content: content.body,
                contentClassification: null,
                type: null,
                folderId: content.folderId,
            };
            map.set(key, { emailBody, dashboards: [] });
        }

        // Sort by most recent email first
        return Array.from(map.values()).sort((a, b) =>
            moment(b.emailBody.createdAt || b.dashboards[0]?.createdAt).diff(
                moment(a.emailBody.createdAt || a.dashboards[0]?.createdAt)
            )
        );
    }, [dashboards, unassignedEmails]);

    // Mirror homepage logic: categorize each dashboard by its latest classified email
    const dashboardCategory = useMemo(() => {
        const map = new Map<string, FilterTab>();
        for (const dashboard of dashboards) {
            const emailItems: EmailBody[] = ArrayUtils.sortByDescending(
                [...(dashboard.emailBodies || []).filter((e: EmailBody) =>
                    ['NEW_DEAL', 'COMPANY_UPDATE', 'NEW_FUND_OPPORTUNITY', 'SHARED_PIPELINE'].includes(e?.contentClassification || '')
                )],
                'createdAt'
            );
            const c = emailItems[0]?.contentClassification;
            const cat: FilterTab =
                c === 'COMPANY_UPDATE' ? "Company updates" :
                    c === 'NEW_FUND_OPPORTUNITY' ? "New fund opportunities" :
                        c === 'SHARED_PIPELINE' ? "Shared pipelines" :
                            "New deals";
            map.set(dashboard.id, cat);
        }
        return map;
    }, [dashboards]);

    const tabCounts = useMemo(() => {
        const counts: Record<FilterTab, number> = { "All": dashboards.length, "New deals": 0, "Company updates": 0, "Shared pipelines": 0, "New fund opportunities": 0 };
        dashboardCategory.forEach((cat) => { counts[cat as FilterTab]++; });
        return counts;
    }, [dashboards, dashboardCategory]);

    const filteredItems = useMemo<AuditEmail[]>(() => {
        let list = auditEmails;

        if (activeFilter !== "All") {
            list = list.filter(({ dashboards: ds }) =>
                ds.some(d => dashboardCategory.get(d.id) === activeFilter)
            );
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(({ dashboards: ds, emailBody }) =>
                ds.some(d => d.title.toLowerCase().includes(q)) ||
                (emailBody.subject ?? "").toLowerCase().includes(q) ||
                (emailBody.from ?? "").toLowerCase().includes(q)
            );
        }

        return list;
    }, [auditEmails, activeFilter, searchQuery, dashboardCategory]);

    const hasMultipleFilterOptions = useMemo(() =>
        FILTER_TABS.filter(t => t !== "All").filter(t => tabCounts[t] > 0).length > 1,
        [tabCounts]
    );


    const handleSelectEmail = useCallback((item: AuditEmail) => {
        dispatch({ type: "SELECT_EMAIL", item });
    }, []);

    const handleSelectDashboard = useCallback((d: Dashboard | null, emailType: string, dealSource: string) => {
        dispatch({ type: "SELECT_DASHBOARD", dashboard: d, emailType, dealSource });
    }, []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }, []);

    const handleFilterClose = useCallback(() => {
        handleSelectDashboard(null, "", "");
    }, [handleSelectDashboard]);

    return (
        <Container className={classes.root}>
            {/* Header */}
            <Box className={classes.header}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography className={classes.headerTitle}>Email Index</Typography>
                        <Typography className={classes.headerSubtitle}>
                            Review forwarded emails and extracted company data
                        </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box className={classes.searchBox}>
                            <SearchIcon />
                            <InputBase
                                className={classes.searchInput}
                                placeholder="Search emails or companies…"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                inputProps={{ style: { padding: 0 } }}
                            />
                        </Box>
                        <Typography className={classes.headerStats}>
                            <strong style={{ color: "#111827" }}>{filteredItems.length}</strong> emails
                            {" · "}
                            <strong style={{ color: "#111827" }}>{tabCounts[activeFilter]}</strong> companies
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

            {/* Filter tabs */}
            {hasMultipleFilterOptions && (
                <Box className={classes.filterTabs}>
                    <Box className={classes.filterTabsIsland}>
                        <Tabs
                            className={classes.filterTabsRoot}
                            value={FILTER_TABS.indexOf(activeFilter)}
                            onChange={(_: React.SyntheticEvent, idx: number) => setActiveFilter(FILTER_TABS[idx])}
                        >
                            {FILTER_TABS.map((tab) => {
                                const isActive = activeFilter === tab;
                                return (
                                    <Tab
                                        key={tab}
                                        className={classes.filterTab}
                                        label={
                                            <Stack direction="row" alignItems="center" spacing={1}
                                                sx={{ textTransform: "capitalize" }}>
                                                {tab}
                                                <Badge
                                                    className={isActive ? classes.filterTabBadgeSelected : classes.filterTabBadge}
                                                    badgeContent={tabCounts[tab]}
                                                    max={Infinity}
                                                />
                                            </Stack>
                                        }
                                    />
                                );
                            })}
                        </Tabs>
                    </Box>
                </Box>
            )}

            {/* 3-panel body */}
            <Box className={classes.body}>
                {!dashboardsLoaded ? (
                    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: "1rem", color: "#9CA3AF", fontFamily: "Inter" }}>
                            Loading…
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <EmailIndexPanel
                            items={filteredItems}
                            selectedItem={selectedItem}
                            selectedDashboard={selectedDashboard}
                            onSelectEmail={handleSelectEmail}
                            onSelectDashboard={handleSelectDashboard}
                        />
                        <EmailDetailPanel
                            item={selectedItem}
                            selectedDashboard={selectedDashboard}
                            selectedCompanyMeta={selectedCompanyMeta}
                            onSelectDashboard={handleSelectDashboard}
                        />
                        <CompanyDetailPanel
                            dashboard={selectedDashboard}
                            selectedItem={selectedItem}
                            emailType={selectedCompanyMeta?.emailType ?? ""}
                            dealSource={selectedCompanyMeta?.dealSource ?? ""}
                            onClose={handleFilterClose}
                        />
                    </>
                )}
            </Box>
        </Container>
    );
};

export default AuditPage;
